import type { JSONSchema7 } from 'json-schema';

import type * as api from '@gitbook/api-client';

import siteVisitorMetaSchema from './meta-schemas/site-adaptive-json-schema-meta-schema.json';

export const SITE_VISITOR_JSON_SCHEMA_META_SCHEMA = siteVisitorMetaSchema;

// TODO-ADAPTIVE-CONTENT: Disallow creation of dynamic props in the UI for now until we add support for
// dynamic expression in GBO side. Remove the below when we added support:
// https://linear.app/gitbook-x/issue/RND-7488/add-dynamic-expressions-support-in-expression-gboruntime
export const SITE_VISITOR_DETERMINISTIC_JSON_SCHEMA_META_SCHEMA = {
    type: 'object',
    $id: 'site-adaptive-schema.json',
    description: 'JSON schema describing the expected attributes of an Adaptive site schema.',
    properties: {
        $schema: {
            type: 'string',
            enum: ['http://json-schema.org/draft-07/schema#'],
        },
        type: {
            type: 'string',
            enum: ['object'],
        },
        properties: {
            type: 'object',
            properties: {
                unsigned: {
                    type: 'object',
                    description: 'Unsigned claims of the site visitor.',
                    properties: {
                        type: {
                            type: 'string',
                            enum: ['object'],
                        },
                        description: {
                            type: 'string',
                            enum: ['Unsigned claims of the site visitor.'],
                        },
                        properties: {
                            type: 'object',
                            additionalProperties: {
                                $ref: '#/definitions/SiteAdaptiveJSONSchemaClaimsProperties',
                            },
                        },
                        additionalProperties: {
                            type: 'boolean',
                            enum: [false],
                        },
                    },
                    required: ['type', 'description', 'properties', 'additionalProperties'],
                    additionalProperties: false,
                },
            },
            additionalProperties: {
                $ref: '#/definitions/SiteAdaptiveJSONSchemaClaimsProperties',
            },
        },
        additionalProperties: {
            type: 'boolean',
            enum: [false],
        },
    },
    required: ['type', 'properties', 'additionalProperties'],
    additionalProperties: false,
    definitions: {
        SiteAdaptiveJSONSchemaClaimsProperties: {
            oneOf: [
                {
                    type: 'object',
                    properties: {
                        type: {
                            type: 'string',
                            enum: ['boolean'],
                        },
                        description: {
                            type: 'string',
                        },
                    },
                    required: ['type', 'description'],
                    additionalProperties: false,
                },
                {
                    type: 'object',
                    properties: {
                        type: {
                            type: 'string',
                            enum: ['string'],
                        },
                        enum: {
                            type: 'array',
                            items: {
                                type: 'string',
                            },
                        },
                        description: {
                            type: 'string',
                        },
                    },
                    required: ['type', 'enum', 'description'],
                    additionalProperties: false,
                },
                {
                    type: 'object',
                    properties: {
                        type: {
                            type: 'string',
                            enum: ['object'],
                        },
                        description: {
                            type: 'string',
                        },
                        properties: {
                            type: 'object',
                            additionalProperties: {
                                $ref: '#/definitions/SiteAdaptiveJSONSchemaClaimsProperties',
                            },
                        },
                        additionalProperties: {
                            type: 'boolean',
                            enum: [false],
                        },
                    },
                    required: ['type', 'description', 'properties', 'additionalProperties'],
                    additionalProperties: false,
                },
            ],
        },
    },
};

export const DEFAULT_SITE_ADAPTIVE_SCHEMA: api.SiteAdaptiveJSONSchema = {
    type: 'object',
    properties: {
        isLoggedIn: {
            type: 'boolean',
            description: 'Whether the user is logged in to the application.',
        },
        isBetaUser: {
            type: 'boolean',
            description: 'Whether the user is a beta user.',
        },
        plan: {
            type: 'string',
            description: `The user's current subscription plan.`,
            enum: ['free', 'plus', 'enterprise'],
        },
        unsigned: {
            type: 'object',
            description: 'Unsigned claims of the site visitor.',
            properties: {
                programmingLanguage: {
                    type: 'string',
                    description: 'The preferred programming language of the visitor',
                    enum: ['python', 'javascript', 'java', 'go'],
                },
            },
            additionalProperties: false,
        },
    },
    additionalProperties: false,
} as const;

export const EMPTY_JSON_SCHEMA: JSONSchema7 = {
    type: 'object',
    properties: {},
    additionalProperties: false,
};

export * from './schemas';
